import {
  EventTriggerConfig,
  RoutineRunAs,
  TimeTriggerConfig,
  TriggerType,
  withCanonicalObjectScopes,
} from './RoutineTypes';
import {deriveAccessLevel, RoutineFormState} from './useRoutineForm';

export function isTriggerComplete(formState: RoutineFormState): boolean {
  if (formState.triggerType == null) {
    return false;
  }
  if (formState.triggerType === TriggerType.TIME_BASED) {
    const config = formState.triggerConfig as Partial<TimeTriggerConfig>;
    const hasTime = (config.run_time ?? '') !== '';
    if (config.freq_type === 'weekly') {
      return (config.weekly_days ?? []).length > 0 && hasTime;
    }
    return hasTime;
  }
  const config = formState.triggerConfig as Partial<EventTriggerConfig>;
  const hasObjectType = (config.object_type ?? '') !== '';
  const validSchemaScope = config.schema_id == null || config.schema_id !== '';
  const validInstanceScope = config.object_ids == null || config.object_ids.length > 0;
  if (!hasObjectType || !validSchemaScope || !validInstanceScope) {
    return false;
  }
  // Event triggers require at least one object scope. The normalizer drops an empty selection, so
  // an absent object_scopes here means the user cleared it.
  if ((config.object_scopes ?? []).length === 0) {
    return false;
  }
  const triggerKind = config.trigger_kind;
  if (triggerKind == null) {
    return false;
  }
  if (triggerKind === 'object_created') {
    return true;
  }
  const hasField = (config.field ?? '') !== '';
  const hasValues = (config.to_values ?? []).length > 0;
  return hasField && hasValues;
}

export function sanitizeTriggerConfig(
  triggerType: TriggerType,
  config: Partial<TimeTriggerConfig> | Partial<EventTriggerConfig>
): TimeTriggerConfig | EventTriggerConfig {
  if (triggerType === TriggerType.TIME_BASED) {
    const timeConfig = config as Partial<TimeTriggerConfig>;
    if (timeConfig.freq_type !== 'weekly') {
      const {weekly_days: _, ...rest} = timeConfig;
      return rest as unknown as TimeTriggerConfig;
    }
    return timeConfig as unknown as TimeTriggerConfig;
  }
  // Persist object_scopes in canonical order so equal selections are stored consistently.
  return withCanonicalObjectScopes(config) as EventTriggerConfig;
}

export interface CanSaveRoutineArgs {
  formState: RoutineFormState;
  isEdit: boolean;
  isDirty: boolean;
  isSaving: boolean;
  isUploading?: boolean;
  currentRunAs?: RoutineRunAs;
}

// Full access limited to a destination folder requires a folder. While the "Limit to a destination
// folder" checkbox is on but no folder has been picked, agentWorkspaceFolderId is the empty-string
// marker (see useRoutineForm), which derives to 'targeted' but is not a savable folder.
export function isAccessSelectionComplete(formState: RoutineFormState): boolean {
  const accessLevel = deriveAccessLevel(
    formState.allowedApprovalCategories,
    formState.agentWorkspaceFolderId
  );
  if (accessLevel === 'targeted') {
    return (formState.agentWorkspaceFolderId ?? '') !== '';
  }
  return true;
}

export function canSaveRoutine({
  formState,
  isEdit,
  isDirty,
  isSaving,
  isUploading = false,
  currentRunAs,
}: CanSaveRoutineArgs): boolean {
  if (isUploading) {
    return false;
  }
  const hasRequiredFields =
    formState.name.trim() !== '' &&
    formState.prompt.trim() !== '' &&
    isTriggerComplete(formState) &&
    isAccessSelectionComplete(formState);
  if (formState.overrideRunAs != null) {
    return false;
  }
  return isEdit
    ? isDirty && !isSaving && hasRequiredFields && currentRunAs === formState.runAs
    : hasRequiredFields && !isSaving;
}
